import { useState, useCallback } from 'react';
import {
  createFileRoute,
  Link,
  Outlet,
  useChildMatches,
  useRouter,
  useNavigate,
} from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Trash2, Mail, MoreVertical, Pencil, Power } from 'lucide-react';

import { apiGet, apiPost, operationsClient } from '@/lib/api';

import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/atoms/avatar';
import { Card, CardContent } from '@/components/atoms/card';
import { Skeleton } from '@/components/atoms/skeleton';
import { StatusBadge } from '@/components/molecules/StatusBadge';
import { useTechnicianRemoval } from '@/hooks/technicians/useTechnicianRemoval';
import { useApiQuery } from '@/hooks/useApiQuery';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { DetailPageLayout } from '@/components/layouts';
import { cn } from '@/lib/utils';
import { schedulerKeys } from '@/lib/queryKeys';
import {
  TechnicianSectionNav,
  TECHNICIAN_SECTION_IDS,
  type TechnicianSectionId,
} from '@/components/organisms/technicians/detail/TechnicianSectionNav';
import { TechnicianSearchSwitcher } from '@/components/organisms/technicians/detail/TechnicianSearchSwitcher';
import { TechnicianInfoSidebar } from '@/components/organisms/technicians/detail/TechnicianInfoSidebar';
import { TechnicianPaySection } from '@/components/organisms/technicians/detail/TechnicianPaySection';
import { TechnicianScheduleSection } from '@/components/organisms/technicians/detail/TechnicianScheduleSection';
import { TechnicianCoverageSection } from '@/components/organisms/technicians/detail/TechnicianCoverageSection';
import { TechnicianCapacitySection } from '@/components/organisms/technicians/detail/TechnicianCapacitySection';
import { TechnicianPerformanceStrip } from '@/components/organisms/technicians/detail/TechnicianPerformanceStrip';
import { TechnicianPerformanceReport } from '@/components/organisms/technicians/detail/TechnicianPerformanceReport';
import { UpcomingJobs } from '@/components/organisms/technicians/detail/UpcomingJobs';
import { TechnicianJobsSection } from '@/components/organisms/technicians/detail/TechnicianJobsSection';
import { useTimeOffRequests } from '@/hooks/technicians/useTimeOff';
import type { TechnicianDetail } from '@/components/organisms/technicians/detail/types';

export const Route = createFileRoute('/_dashboard/dashboard/technicians/$id')({
  component: TechnicianDetailPage,
  validateSearch: (search: Record<string, unknown>): { tab?: TechnicianSectionId } => ({
    tab: TECHNICIAN_SECTION_IDS.includes(search.tab as TechnicianSectionId)
      ? (search.tab as TechnicianSectionId)
      : undefined,
  }),
});

function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function DetailSkeleton() {
  return (
    <div className="space-y-4 p-5">
      <Skeleton className="h-8 w-32" />
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-14 w-14 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-3 w-56" />
            </div>
          </div>
        </CardContent>
      </Card>
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function TechnicianDetailPage() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const router = useRouter();
  const navigate = useNavigate();
  const childMatches = useChildMatches();
  const queryClient = useQueryClient();

  const activeSection: TechnicianSectionId = tab ?? 'summary';
  const setActiveSection = useCallback(
    (next: TechnicianSectionId) => {
      void navigate({ to: '.', search: { tab: next }, replace: true });
    },
    [navigate],
  );

  const [inviting, setInviting] = useState(false);

  const technicianQuery = useApiQuery<TechnicianDetail>({
    queryKey: schedulerKeys.detail(id),
    queryFn: () => apiGet<TechnicianDetail>(operationsClient, `/technicians/${id}`),
    enabled: Boolean(id),
  });
  const tech = technicianQuery.data ?? null;
  const loading = technicianQuery.isLoading;

  const crewName = tech?.crew?.name;

  const { data: timeOff = [] } = useTimeOffRequests(id);
  const pendingTimeOff = timeOff.filter((r) => r.status === 'PENDING').length;

  const fetchTechnician = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: schedulerKeys.detail(id) });
  }, [id, queryClient]);

  // GAP-02 — the shared deactivate/delete flow (choice, never a block). Same
  // source of truth as the roster row actions so the two can't diverge.
  const { requestDeactivate, requestDelete, activate, checking, isMutating, dialogs } =
    useTechnicianRemoval(
      { id, name: tech?.name ?? 'this technician' },
      {
        onDeleted: () => void router.navigate({ to: '/dashboard/technicians' }),
        onChanged: fetchTechnician,
      },
    );

  if (childMatches.length > 0) {
    return <Outlet />;
  }

  const handleInvite = async () => {
    if (!tech) return;
    setInviting(true);
    try {
      await apiPost(operationsClient, `/technicians/${tech.id}/invite`, {});
      toast.success('Invite sent to ' + tech.email);
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.technicians() });
      fetchTechnician();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  // Deactivate/delete now run through useTechnicianRemoval (requestDeactivate /
  // requestDelete / activate) — the shared choice-not-block flow.
  const toggleActive = () => (tech?.isActive ? requestDeactivate() : activate());

  function renderSection(t: TechnicianDetail) {
    switch (activeSection) {
      case 'summary':
        return <TechnicianSummarySection tech={t} />;
      case 'skills-coverage':
        return <TechnicianCoverageSection technician={t} onChanged={fetchTechnician} />;
      case 'capacity':
        return <TechnicianCapacitySection technician={t} onChanged={fetchTechnician} />;
      case 'schedule':
        return <TechnicianScheduleSection technician={t} onChanged={fetchTechnician} />;
      case 'pay':
        return <TechnicianPaySection technician={t} onChanged={fetchTechnician} />;
      case 'jobs':
        return <TechnicianJobsSection technicianId={t.id} />;
      case 'performance':
        return <TechnicianPerformanceReport fixedTechnicianId={t.id} embedded />;
      default:
        return null;
    }
  }

  return (
    <>
      <DetailPageLayout
        key={id}
        headerClassName="block bg-background p-0"
        contentClassName={cn('p-0', !loading && tech && 'flex space-y-0 overflow-hidden')}
        header={
          <div className="flex min-h-16 items-center gap-2.5 border-b px-3 py-2 sm:px-4">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="shrink-0"
              onClick={() => void router.navigate({ to: '/dashboard/technicians' })}
              title="Back to technicians"
              aria-label="Back to technicians"
            >
              <ArrowLeft className="size-4" />
            </Button>
            <div className="h-8 w-px shrink-0 bg-border" />
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {loading || !tech ? (
                <>
                  <Skeleton className="size-10 shrink-0 rounded-lg" />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="h-3 w-52 max-w-full" />
                  </div>
                </>
              ) : (
                <>
                  <Avatar className="size-11 shrink-0 rounded-lg">
                    {tech.photoUrl ? (
                      <AvatarImage src={tech.photoUrl} alt="" className="object-cover" />
                    ) : null}
                    <AvatarFallback className="rounded-lg bg-primary/10 text-sm font-semibold text-primary">
                      {getInitials(tech.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <h1 className="truncate text-lg font-bold">{tech.name}</h1>
                      <StatusBadge status={tech.isActive ? 'ACTIVE' : 'INACTIVE'} />
                      {pendingTimeOff > 0 ? (
                        <Badge
                          variant="outline"
                          className="hidden border-amber-300 text-amber-700 md:inline-flex dark:border-amber-800 dark:text-amber-400"
                        >
                          {pendingTimeOff} time-off request
                          {pendingTimeOff === 1 ? '' : 's'}
                        </Badge>
                      ) : null}
                    </div>
                    <div className="mt-0.5 hidden min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground sm:flex">
                      <span className="truncate">{tech.email}</span>
                      <span>·</span>
                      <span>{tech.authUserId ? 'Account connected' : 'No account'}</span>
                      {crewName ? (
                        <>
                          <span>·</span>
                          <span className="truncate">{crewName}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <TechnicianSearchSwitcher compact />
              {tech ? (
                <>
                  <Button variant="outline" size="sm" className="hidden lg:inline-flex" asChild>
                    <Link to="/dashboard/technicians/$id/edit" params={{ id: tech.id }}>
                      <Pencil className="mr-1.5 size-3.5" />
                      Edit profile
                    </Link>
                  </Button>
                  {!tech.authUserId ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="hidden lg:inline-flex"
                      onClick={handleInvite}
                      disabled={inviting}
                    >
                      {inviting ? (
                        <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                      ) : (
                        <Mail className="mr-1.5 size-3.5" />
                      )}
                      Invite
                    </Button>
                  ) : null}
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      'hidden lg:inline-flex',
                      tech.isActive && 'text-destructive hover:bg-destructive/10',
                      !tech.isActive && 'text-emerald-700 hover:bg-emerald-50',
                    )}
                    onClick={toggleActive}
                    disabled={isMutating || checking !== null}
                  >
                    {checking === 'deactivate' ? (
                      <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                    ) : (
                      <Power className="mr-1.5 size-3.5" />
                    )}
                    {tech.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon-sm"
                    className="hidden text-destructive hover:bg-destructive/10 lg:inline-flex"
                    onClick={() => requestDelete()}
                    disabled={checking !== null}
                    title="Delete technician"
                    aria-label="Delete technician"
                  >
                    {checking === 'delete' ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon-sm"
                        className="lg:hidden"
                        disabled={inviting || isMutating || checking !== null}
                        title="Technician actions"
                        aria-label="Technician actions"
                      >
                        <MoreVertical size={14} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem
                        onSelect={() =>
                          void router.navigate({
                            to: '/dashboard/technicians/$id/edit',
                            params: { id: tech.id },
                          })
                        }
                      >
                        <Pencil className="mr-2 size-3.5" />
                        Edit
                      </DropdownMenuItem>
                      {!tech.authUserId ? (
                        <DropdownMenuItem onSelect={handleInvite} disabled={inviting}>
                          <Mail className="mr-2 size-3.5" />
                          Invite account
                        </DropdownMenuItem>
                      ) : null}
                      <DropdownMenuItem
                        onSelect={toggleActive}
                        disabled={isMutating || checking !== null}
                      >
                        <Power className="mr-2 size-3.5" />
                        {tech.isActive ? 'Deactivate' : 'Activate'}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        variant="destructive"
                        onSelect={() => requestDelete()}
                        disabled={checking !== null}
                      >
                        <Trash2 className="mr-2 size-3.5" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : null}
            </div>
          </div>
        }
      >
        {loading ? (
          <DetailSkeleton />
        ) : !tech ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center">
            <p className="text-sm text-destructive">Technician not found</p>
          </div>
        ) : (
          <>
            <TechnicianSectionNav
              active={activeSection}
              onSelect={setActiveSection}
              counts={{ timeOff: pendingTimeOff }}
            />
            <div className="min-w-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
              {renderSection(tech)}
            </div>
            {activeSection === 'summary' ? (
              <TechnicianInfoSidebar
                technician={tech}
                onViewCoverage={() => setActiveSection('skills-coverage')}
              />
            ) : null}
          </>
        )}
      </DetailPageLayout>

      {/* GAP-02 — the shared choice-not-block dialogs (same as the roster table). */}
      {dialogs}
    </>
  );
}

function TechnicianSummarySection({ tech }: { tech: TechnicianDetail }) {
  return (
    <>
      {tech.bio?.trim() ? (
        <Card>
          <CardContent className="p-4">
            <p className="mb-1.5 font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              About
            </p>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {tech.bio}
            </p>
          </CardContent>
        </Card>
      ) : null}
      <TechnicianPerformanceStrip technicianId={tech.id} />
      <UpcomingJobs technicianId={tech.id} />
    </>
  );
}
